// A small, dependency-free Markdown renderer for the release changelog.
//
// The changelog_md strings are agent-authored: ATX headings (`##`/`###`),
// blank-line-separated paragraphs, `-`/`*` bullet lists, and the inline marks
// **bold**, *italic*, `code`, and [text](url). We render React nodes directly
// (never dangerouslySetInnerHTML), so the output is XSS-safe by construction —
// link hrefs are restricted to http(s) and any unmatched markup degrades to
// literal text. Anything fancier than the grammar above falls through as plain
// text rather than breaking the page.

import type { ReactNode } from 'react'

// ── Inline parsing ──────────────────────────────────────────────────────────

// Only allow safe link schemes; anything else renders as plain text.
function safeHref(url: string): string | null {
  const u = url.trim()
  if (/^https?:\/\//i.test(u) || u.startsWith('/') || u.startsWith('#')) return u
  return null
}

// Parse the inline marks within one line of text into React nodes. Precedence
// is code > link > bold > italic; unmatched delimiters stay literal.
function parseInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = []
  let rest = text
  let k = 0
  // Ordered so `**` is tried before `*` (bold before italic).
  const patterns: { re: RegExp; render: (m: RegExpExecArray, key: string) => ReactNode }[] = [
    {
      re: /`([^`]+)`/,
      render: (m, key) => <code key={key}>{m[1]}</code>,
    },
    {
      re: /\[([^\]]+)\]\(([^)\s]+)\)/,
      render: (m, key) => {
        const href = safeHref(m[2])
        return href ? (
          <a key={key} href={href} rel="noopener noreferrer">
            {m[1]}
          </a>
        ) : (
          <span key={key}>{m[0]}</span>
        )
      },
    },
    {
      re: /\*\*([^*]+)\*\*/,
      render: (m, key) => <strong key={key}>{m[1]}</strong>,
    },
    {
      re: /\*([^*]+)\*/,
      render: (m, key) => <em key={key}>{m[1]}</em>,
    },
  ]

  // Repeatedly find the earliest-matching delimiter and split around it.
  while (rest.length > 0) {
    let best: { idx: number; m: RegExpExecArray; render: (m: RegExpExecArray, key: string) => ReactNode } | null =
      null
    for (const p of patterns) {
      const m = p.re.exec(rest)
      if (m && (best === null || m.index < best.idx)) {
        best = { idx: m.index, m, render: p.render }
      }
    }
    if (best === null) {
      nodes.push(rest)
      break
    }
    if (best.idx > 0) nodes.push(rest.slice(0, best.idx))
    nodes.push(best.render(best.m, `${keyPrefix}-${k++}`))
    rest = rest.slice(best.idx + best.m[0].length)
  }
  return nodes
}

// ── Block parsing ───────────────────────────────────────────────────────────

export function Markdown({ source, className }: { source: string; className?: string }) {
  const lines = source.replace(/\r\n/g, '\n').split('\n')
  const blocks: ReactNode[] = []
  let para: string[] = []
  let list: string[] = []
  let key = 0

  const flushPara = () => {
    if (para.length === 0) return
    const text = para.join(' ')
    blocks.push(<p key={`p-${key++}`}>{parseInline(text, `p${key}`)}</p>)
    para = []
  }
  const flushList = () => {
    if (list.length === 0) return
    blocks.push(
      <ul key={`ul-${key++}`}>
        {list.map((item, i) => (
          <li key={i}>{parseInline(item, `li${key}-${i}`)}</li>
        ))}
      </ul>,
    )
    list = []
  }

  for (const raw of lines) {
    const line = raw.trimEnd()
    const heading = /^(#{1,6})\s+(.*)$/.exec(line)
    const bullet = /^[-*]\s+(.*)$/.exec(line)

    if (heading) {
      flushPara()
      flushList()
      // Each release is an <article> headed by an <h3> (the version). Content
      // headings nest below that, so a top-level `#`/`##` in the body starts at
      // <h4> — keeps one h3 per release and a sane document outline.
      const level = Math.min(heading[1].length, 6)
      const Tag = `h${Math.min(level + 2, 6)}` as 'h4' | 'h5' | 'h6'
      blocks.push(<Tag key={`h-${key++}`}>{parseInline(heading[2], `h${key}`)}</Tag>)
    } else if (bullet) {
      flushPara()
      list.push(bullet[1])
    } else if (line.trim() === '') {
      flushPara()
      flushList()
    } else {
      flushList()
      para.push(line.trim())
    }
  }
  flushPara()
  flushList()

  return <div className={className}>{blocks}</div>
}
